import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { addToWaitingList } from "@/data";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Rocket, CheckCircle2 } from "lucide-react";

export function WaitingPageScreen() {
  const [email, setEmail] = useState("");

  const mutation = useMutation({
    mutationFn: (email) => addToWaitingList(email),
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "You've been added to the waiting list.",
        variant: "success",
      });
      setEmail("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Something went wrong: ${error.response?.data?.message || error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    mutation.mutate(email);
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 p-4">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">

        <div className="text-white space-y-6">
          <div className="inline-flex items-center justify-center p-2 bg-primary/20 rounded-lg mb-4">
            <Rocket className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
            Something amazing is coming soon.
          </h1>
          <p className="text-xl text-gray-300">
            Be the first to know when we launch. Join our exclusive waiting list and get early access.
          </p>

          <ul className="space-y-3 pt-4">
            {[
              "Early access to new features",
              "Exclusive founder pricing",
              "Priority support"
            ].map((item, i) => (
              <li key={i} className="flex items-center text-gray-300">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-3" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <Card className="w-full bg-white/10 border-white/20 backdrop-blur-sm text-white">
          <CardHeader>
            <CardTitle className="text-2xl">Join the Waitlist</CardTitle>
            <CardDescription className="text-gray-300">
              Enter your email to get notified when we launch.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-400 focus-visible:ring-primary"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={mutation.isLoading}
              >
                {mutation.isLoading ? "Joining..." : "Join Waitlist"}
              </Button>
              <p className="text-xs text-center text-gray-400 mt-4">
                We respect your privacy. No spam, ever.
              </p>
            </form>
          </CardContent>
        </Card>

      </div>
    </section>
  );
}
